import loadComposeInfoBars from './loadComposeInfoBars';
import { applySmimeSettings, onSmimeModeEnabled } from './smimeActions';
import prepareComposeViewStateForPopout from './prepareComposeViewStateForPopout';
import closeCompose from '../actions/closeCompose';
import triggerPolicyTips from '../actions/triggerPolicyTips';
import {
    ComposeViewState,
    ComposeOperation,
    ComposeTarget,
    getStore,
} from 'owa-mail-compose-store';
import ensureComposeTabHandler from '../utils/composeTabHandler';
import createGroupComposeViewState from '../utils/createGroupComposeViewState';
import createPublicFolderComposeViewState from '../utils/createPublicFolderComposeViewState';
import createSharedFolderComposeViewState from '../utils/createSharedFolderComposeViewState';
import { getAllRecipientsAsEmailAddressStrings } from '../utils/getAllRecipientsAsEmailAddressStrings';
import getCurrentFromAddress from '../utils/getFromAddressFromRecipientWell';
import loadAttachmentSuggestions from '../utils/loadAttachmentSuggestions';
import { AttachmentFileAttributes } from 'owa-attachment-file-types';
import { getMRUAttachmentStore } from 'owa-attachment-suggestions';
import startSession from 'owa-controls-findpeople-feedback-manager/lib/actions/startSession';
import isEncryptionEnabled from 'owa-encryption-common/lib/utils/isEncryptionEnabled';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isPublicFolder } from 'owa-folders';
import { onComposeOpened } from 'owa-mail-actions/lib/composeActions';
import { openComposeActionName } from 'owa-mail-compose-action-names';
import { ReplyWithAttachmentSuggestion } from 'owa-mail-store';
import updateMailtips from 'owa-mail-tips/lib/actions/updateMailtips';
import { isPolicyTipsEnabled } from 'owa-policy-tips/lib/utils/isPolicyTipsEnabled';
import { AddPopoutSource, addPopoutV2 } from 'owa-popout-v2';
import EventTrigger from 'owa-service/lib/contract/EventTrigger';
import RightsManagementLicenseDataType from 'owa-service/lib/contract/RightsManagementLicenseDataType';
import getComplianceConfigurationOperation from 'owa-service/lib/operation/getComplianceConfigurationOperation';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { isSmimeSettingEnabled } from 'owa-smime/lib/utils/smimeSettingsUtil';
import { lazyOpenComposeInSxS } from 'owa-sxs-store';
import { lazyAddTab, TabType } from 'owa-tab-store';
import { action } from 'satcheljs/lib/legacy';
import createComposeViewState, {
    ComposeViewStateInitProps,
    ComposeViewStateInitPropsInSync,
    createComposeViewStateInSync,
} from '../utils/createComposeViewState';
import isCLPEnabled from 'owa-mail-clp/lib/utils/isCLPEnabled';
import { lazyTriggerCLPAutoLabeling } from 'owa-mail-clp';

const openComposeWithViewState = action('openComposeWithViewState')(
    function openComposeWithViewState(
        viewState: ComposeViewState,
        openTarget: ComposeTarget,
        popoutSource?: AddPopoutSource
    ) {
        if (openTarget == ComposeTarget.Popout) {
            const popoutData = prepareComposeViewStateForPopout(viewState);
            addPopoutV2('mail', 'compose', () => {
                closeCompose(viewState);
                return Promise.resolve(popoutData);
            });

            return;
        }

        const store = getStore();
        if (openTarget == ComposeTarget.PrimaryReadingPane && store.primaryComposeId) {
            // There is still default compose before open a new one, we should not overwrite it.
            // Need to call trySaveAndCloseCompose() first.
            return;
        }

        store.viewStates.set(viewState.composeId, viewState);

        if (openTarget == ComposeTarget.SecondaryTab || viewState.isInlineCompose) {
            ensureComposeTabHandler();

            lazyAddTab.importAndExecute(
                TabType.Compose,
                !viewState.isInlineCompose /*isShown*/,
                !viewState.isInlineCompose /*makeActive*/,
                viewState.composeId
            );
        } else if (openTarget == ComposeTarget.SxS) {
            lazyOpenComposeInSxS.import().then(openComposeInSxS => openComposeInSxS(viewState));
        } else {
            store.primaryComposeId = viewState.composeId;
        }

        if (isEncryptionEnabled()) {
            if (store.complianceConfigurations.size == 0) {
                getComplianceConfigurationOperation().then(result => {
                    result.RmsTemplates.forEach(template => {
                        const rmsTemplate: RightsManagementLicenseDataType = {
                            TemplateDescription: template.Description,
                            TemplateName: template.Name,
                            RmsTemplateId: template.Id,
                        };
                        store.complianceConfigurations.set(template.Id, rmsTemplate);
                    });
                    if (isFeatureEnabled('cmp-message-classification')) {
                        result.MessageClassifications.forEach(classification => {
                            store.messageClassifications.set(classification.Id, classification);
                        });
                    }
                });
            }
        }

        if (viewState.operation == ComposeOperation.EditDraft) {
            if (isPolicyTipsEnabled()) {
                triggerPolicyTips(viewState, EventTrigger.OpenDraft);
            }

            if (isCLPEnabled() && isFeatureEnabled('cmp-clp-auto-labeling')) {
                lazyTriggerCLPAutoLabeling.import().then(triggerCLPAutoLabeling => {
                    triggerCLPAutoLabeling(
                        viewState,
                        viewState.clpViewState,
                        viewState.itemId,
                        viewState.content
                    );
                });
            }
        }

        const recipientEmailAddresses: string[] = getAllRecipientsAsEmailAddressStrings(viewState);
        startSession(viewState.findPeopleFeedbackManager, recipientEmailAddresses);

        const fromEmailAddress = getCurrentFromAddress(viewState.toRecipientWell);
        updateMailtips(viewState.composeId, fromEmailAddress, recipientEmailAddresses);

        if (isFeatureEnabled('cmp-smime')) {
            if (isSmimeEnabledInViewState(viewState.smimeViewState)) {
                onSmimeModeEnabled(viewState);
            }

            // update S/MIME viewState for new compose based on S/MIME admin/user settings
            if (viewState.operation === ComposeOperation.New && isSmimeSettingEnabled()) {
                applySmimeSettings(viewState);
            }
        }

        loadComposeInfoBars(viewState);

        // Raise onComposeOpened action
        onComposeOpened();
    }
);

export { openComposeWithViewState };

export async function prepareComposeViewState(
    initProps: ComposeViewStateInitProps,
    targetId?: string, //target could be publicfolder or group
    attachmentFiles?: AttachmentFileAttributes[]
): Promise<ComposeViewState> {
    const viewState = targetId
        ? isPublicFolder(targetId)
            ? await createPublicFolderComposeViewState({
                  publicFolderId: targetId,
                  ...initProps,
              })
            : await createGroupComposeViewState({
                  groupId: targetId,
                  ...initProps,
              })
        : initProps.isInSharedFolder
        ? await createSharedFolderComposeViewState(initProps)
        : await createComposeViewState(initProps);

    if (attachmentFiles) {
        viewState.attachmentFilesToUpload = attachmentFiles;
    }

    // Preload attachment suggestions if no MRU attachments have already been stored
    const mruSuggestions: ReplyWithAttachmentSuggestion[] = getMRUAttachmentStore().attachments;

    if (!mruSuggestions || mruSuggestions.length == 0) {
        loadAttachmentSuggestions(viewState);
    }

    return viewState;
}

export default action(openComposeActionName)(async function openCompose(
    initProps: ComposeViewStateInitProps,
    openTarget: ComposeTarget,
    targetId?: string,
    attachmentFiles?: AttachmentFileAttributes[]
) {
    const viewState = await prepareComposeViewState(initProps, targetId, attachmentFiles);

    openComposeWithViewState(viewState, openTarget);
});

/**
 * A shortcut action to open mail compose in popout window directly.
 * This function doesn't contain any async operation so it is popout-blocker-friendly
 * (Group compose and attachments are not supported here since they still require async load)
 */
export const openMailComposeInPopOut = action('openMailComposeInPopOut')(
    (initProps: ComposeViewStateInitPropsInSync) => {
        const viewState = createComposeViewStateInSync(initProps);
        openComposeWithViewState(viewState, ComposeTarget.Popout);
    }
);

export const openAndSendComposeInPopout = (viewState: ComposeViewState) => {
    const popoutData = prepareComposeViewStateForPopout(viewState, true /* sendOnPopout */);
    addPopoutV2('mail', 'compose', () => {
        closeCompose(viewState);
        return Promise.resolve(popoutData);
    });
    return;
};
